import type { ReactElement } from 'react';
import { Box, IconButton, Slider, Typography, createSvgIcon } from '@mui/material';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import PianoRounded from '@mui/icons-material/PianoRounded';
import RecordVoiceOverRounded from '@mui/icons-material/RecordVoiceOverRounded';
import VolumeUpRounded from '@mui/icons-material/VolumeUpRounded';
import VolumeDownRounded from '@mui/icons-material/VolumeDownRounded';
import VolumeMuteRounded from '@mui/icons-material/VolumeMuteRounded';
import VolumeOffRounded from '@mui/icons-material/VolumeOffRounded';
import { useDispatch, useSelector } from 'react-redux';
import { choiceFocusRingSx } from 'Components/ChoiceGroup/choiceStyles';
import {
  MAX_VOLUME,
  MIN_VOLUME,
  VOLUME_CHANNELS,
  VOLUME_CHANNEL_LABELS,
  VolumeChannel,
} from './volumeSettings';
import { selectVolumeSettings, setChannelMuted, setChannelVolume } from './volumeSlice';
import { volumeControlsAvailable } from './volumeAvailability';
import { TOUCH_TARGET_MIN_SIZE_PX } from 'Utils/touchTarget';

// The metronome from the app's tempo control, drawn as an icon.
const MetronomeRounded = createSvgIcon(
  <path d="M23.6507 16.5967L20.6208 17.4093L16.637 32.3023C16.6105 32.4348 16.584 32.5762 16.584 32.7175C16.584 33.698 17.3702 34.4842 18.3507 34.4842H28.9507C29.9312 34.4842 30.7173 33.698 30.7173 32.7175C30.7173 32.5762 30.6908 32.4348 30.6643 32.3023L29.463 27.7885L28.0673 29.1842L28.244 30.0675H24.8962L27.4048 27.5588L26.1593 26.3133L22.4052 30.0675H19.0573L22.1402 18.5842H25.1612L26.4508 23.3807L27.8907 21.932L26.6805 17.4093L23.6507 16.5967ZM22.9882 19.4675V28.08L24.3132 26.755V19.4675H22.9882ZM30.5318 21.9408L28.032 24.4407L27.4048 23.8135L26.1593 25.0678L28.6503 27.5588L29.9047 26.3133L29.2775 25.6862L31.7773 23.1863L30.5318 21.9408Z" />,
  'MetronomeRounded',
);

const CHANNEL_ICONS: Record<VolumeChannel, ReactElement> = {
  piano: <PianoRounded fontSize="inherit" />,
  metronome: <MetronomeRounded fontSize="inherit" viewBox="14.5 15 19.5 21" />,
  narration: <RecordVoiceOverRounded fontSize="inherit" />,
  sfx: <AutoAwesomeRounded fontSize="inherit" />,
};

// A 6px rounded track: the level filled in the brand blue (a slight gradient), the rest a slate
// that still reads against the dark panels (3:1 or more).
export const SLIDER_TRACK_HEIGHT_PX = 6;
const SLIDER_THUMB_PX = 16;
const TRACK_REST = '#5B6474';
const TRACK_FILL = 'linear-gradient(90deg, #5C74E0 0%, #7E95FF 100%)';
const TRACK_FILL_MUTED = '#7A8294';
const FOCUS_RING = '#A9B6F7';
const FOCUS_GAP = '#050B13';
// On a touch screen the slider answers a strip the full 44px tall around the track.
const sliderTouchPaddingPx = (TOUCH_TARGET_MIN_SIZE_PX - SLIDER_TRACK_HEIGHT_PX) / 2;

// The mute button with a mouse: a round 32px button around a 20px icon.
export const MUTE_BUTTON_SIZE_PX = 32;
const MUTED_COLOR = '#F2A0A0';

// A muted channel's row steps back (icon, name, track and level dimmed, still readable);
// only its mute button keeps full strength.
const rowColours = (muted: boolean) =>
  muted
    ? { icon: '#8D929F', name: '#9AA0AC', level: '#8D929F' }
    : { icon: '#A9B6F7', name: '#ECECED', level: '#ADB1B7' };

const sliderSx = (muted: boolean) =>
  ({
    height: `${SLIDER_TRACK_HEIGHT_PX}px`,
    borderRadius: `${SLIDER_TRACK_HEIGHT_PX / 2}px`,
    '& .MuiSlider-rail': { backgroundColor: TRACK_REST, opacity: 1 },
    '& .MuiSlider-track': {
      border: 'none',
      background: muted ? TRACK_FILL_MUTED : TRACK_FILL,
    },
    '& .MuiSlider-thumb': {
      width: SLIDER_THUMB_PX,
      height: SLIDER_THUMB_PX,
      backgroundColor: muted ? '#B8BDC7' : '#FFFFFF',
      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.55), 0 0 0 2px rgba(5, 11, 19, 0.6)',
      transition: 'width 0.12s ease, height 0.12s ease, box-shadow 0.12s ease',
      '&:hover': {
        width: SLIDER_THUMB_PX + 2,
        height: SLIDER_THUMB_PX + 2,
        boxShadow: `0 1px 4px rgba(0, 0, 0, 0.55), 0 0 0 6px ${
          muted ? 'rgba(141, 146, 159, 0.3)' : 'rgba(111, 135, 241, 0.35)'
        }`,
      },
      // Keyboard focus: a solid ring (10:1 against the dark panels) clear of the thumb by a dark gap.
      '&.Mui-focusVisible': {
        width: SLIDER_THUMB_PX + 2,
        height: SLIDER_THUMB_PX + 2,
        boxShadow: `0 0 0 3px ${FOCUS_GAP}, 0 0 0 5px ${FOCUS_RING}`,
      },
      '&.Mui-active': {
        width: SLIDER_THUMB_PX + 4,
        height: SLIDER_THUMB_PX + 4,
        boxShadow: `0 1px 4px rgba(0, 0, 0, 0.55), 0 0 0 8px ${
          muted ? 'rgba(141, 146, 159, 0.35)' : 'rgba(111, 135, 241, 0.42)'
        }`,
      },
    },
    // On a touch screen the slider answers a 44px strip (the whole track, 44px tall) and its thumb a
    // 44px circle; it looks the same. The thumb's hidden range input (what assistive tech and touch
    // checks read as the control) spans the thumb's touch circle.
    '@media (pointer: coarse)': {
      paddingBlock: `${sliderTouchPaddingPx}px`,
      '& .MuiSlider-thumb::after': {
        width: TOUCH_TARGET_MIN_SIZE_PX,
        height: TOUCH_TARGET_MIN_SIZE_PX,
      },
      // MUI sizes the input inline (100% of the thumb), so only an important rule can widen it.
      '& .MuiSlider-thumb input': {
        width: `${TOUCH_TARGET_MIN_SIZE_PX}px !important`,
        height: `${TOUCH_TARGET_MIN_SIZE_PX}px !important`,
        margin: '0 !important',
        top: `calc(50% - ${TOUCH_TARGET_MIN_SIZE_PX / 2}px)`,
        left: `calc(50% - ${TOUCH_TARGET_MIN_SIZE_PX / 2}px)`,
      },
    },
  }) as const;

// Round, with the muted state in its own colour and fill. On a touch screen it is a 44px circle;
// the negative margin gives the extra back, so the row keeps its size.
const muteButtonSx = (muted: boolean) =>
  ({
    width: `${MUTE_BUTTON_SIZE_PX}px`,
    height: `${MUTE_BUTTON_SIZE_PX}px`,
    padding: 0,
    color: muted ? MUTED_COLOR : '#CED0D4',
    backgroundColor: muted ? 'rgba(242, 160, 160, 0.14)' : 'transparent',
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '&:hover': {
      backgroundColor: muted ? 'rgba(242, 160, 160, 0.22)' : 'rgba(255, 255, 255, 0.08)',
    },
    ...choiceFocusRingSx,
    '@media (pointer: coarse)': {
      minWidth: `${TOUCH_TARGET_MIN_SIZE_PX}px`,
      minHeight: `${TOUCH_TARGET_MIN_SIZE_PX}px`,
      marginInline: `${(MUTE_BUTTON_SIZE_PX - TOUCH_TARGET_MIN_SIZE_PX) / 2}px`,
      marginBlock: `${(MUTE_BUTTON_SIZE_PX - TOUCH_TARGET_MIN_SIZE_PX) / 2}px`,
    },
  }) as const;

// With room (the Settings page), the rows sit in a card; in the small panels (lesson, piece,
// tutorial), which are cards already, they sit on the panel and give the slider the room.
const CARD_FROM_PX = 400;
const roomy = `@container (min-width: ${CARD_FROM_PX}px)`;
// A piece's settings panel on a phone leaves the rows under 290px: the level readout gives its
// room to the slider there (the slider still reports its level to assistive tech).
const READOUT_FROM_PX = 290;
const cramped = `@container (max-width: ${READOUT_FROM_PX - 0.02}px)`;

const listSx = {
  display: 'flex',
  flexDirection: 'column',
  [roomy]: {
    padding: '2px 14px',
    border: '1px solid rgba(255, 255, 255, 0.10)',
    borderRadius: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    '& > div + div': { borderTop: '1px solid rgba(255, 255, 255, 0.06)' },
  },
} as const;

// One line per channel: icon, name, slider, level, mute.
const rowSx = {
  display: 'grid',
  gridTemplateColumns: '18px 86px minmax(40px, 1fr) 36px 32px',
  alignItems: 'center',
  columnGap: '8px',
  minHeight: '44px',
  [roomy]: { gridTemplateColumns: '20px 96px minmax(80px, 1fr) 40px 32px', columnGap: '12px' },
  [cramped]: { gridTemplateColumns: '18px 86px minmax(40px, 1fr) 32px' },
} as const;

const channelIconSx = (muted: boolean) =>
  ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    color: rowColours(muted).icon,
    transition: 'color 0.15s ease',
  }) as const;

const channelNameSx = (muted: boolean) =>
  ({
    fontFamily: 'Lato',
    fontSize: '14px',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    color: rowColours(muted).name,
    transition: 'color 0.15s ease',
  }) as const;

// The level, right-aligned in figures of one width so it does not jiggle as it changes.
const levelReadoutSx = (muted: boolean) =>
  ({
    fontFamily: 'Lato',
    fontSize: '13px',
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right',
    color: rowColours(muted).level,
    [cramped]: { display: 'none' },
  }) as const;

/** The speaker a channel shows: struck through when muted, its waves following the level. */
const ChannelVolumeIcon = ({ level, muted }: { level: number; muted: boolean }) => {
  if (muted) return <VolumeOffRounded fontSize="small" />;
  if (level === MIN_VOLUME) return <VolumeMuteRounded fontSize="small" />;
  if (level < MAX_VOLUME / 2) return <VolumeDownRounded fontSize="small" />;
  return <VolumeUpRounded fontSize="small" />;
};

type VolumeControlsProps = {
  /** A heading above the controls, for surfaces that do not label the section. */
  title?: string;
};

/**
 * One row per sound channel (piano, metronome, narration, sound effects): a
 * level slider and that channel's own mute. There is no mute over every
 * channel. Every surface that shows these renders this one component, so they
 * all edit the same settings.
 */
export default function VolumeControls({ title }: VolumeControlsProps) {
  const dispatch = useDispatch();
  const settings = useSelector(selectVolumeSettings);

  if (!volumeControlsAvailable()) return null;

  const onVolumeChange = (channel: VolumeChannel) => (_event: Event, value: number | number[]) =>
    dispatch(setChannelVolume({ channel, volume: Array.isArray(value) ? value[0] : value }));

  return (
    <Box
      data-testid="volume-controls"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        color: '#ECECED',
        textAlign: 'left',
        containerType: 'inline-size',
      }}
    >
      {title && (
        <Typography variant="body1" sx={{ fontFamily: 'Lato', marginBottom: '6px' }}>
          {title}
        </Typography>
      )}
      <Box sx={listSx}>
        {VOLUME_CHANNELS.map((channel) => {
          const { level, muted } = settings[channel];
          const label = VOLUME_CHANNEL_LABELS[channel];
          return (
            <Box key={channel} data-muted={muted} sx={rowSx}>
              <Box component="span" aria-hidden sx={channelIconSx(muted)}>
                {CHANNEL_ICONS[channel]}
              </Box>
              <Typography variant="body2" sx={channelNameSx(muted)}>
                {label}
              </Typography>
              <Slider
                size="small"
                sx={sliderSx(muted)}
                min={MIN_VOLUME}
                max={MAX_VOLUME}
                step={1}
                value={level}
                onChange={onVolumeChange(channel)}
                slotProps={{ input: { 'aria-label': `${label} volume` } }}
              />
              <Typography variant="body2" sx={levelReadoutSx(muted)}>
                {level}%
              </Typography>
              <IconButton
                size="small"
                aria-label={`Mute ${label}`}
                aria-pressed={muted}
                onClick={() => dispatch(setChannelMuted({ channel, muted: !muted }))}
                sx={muteButtonSx(muted)}
              >
                <ChannelVolumeIcon level={level} muted={muted} />
              </IconButton>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
